import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, MessageCircle, Send, X, Star, BadgePercent,
  Users as UsersIcon, Repeat, Crown,
} from "lucide-react";

const DEFAULT_TEMPLATE =
  "Halo {phone} 👋\n\nTerima kasih sudah jadi pelanggan setia *Java SPA Riyadh* ✨\n\nKami punya penawaran spesial minggu ini — diskon 15% untuk semua layanan VIP. Pesan sekarang sebelum kuota habis!\n\n📍 Riyadh · Buka 24 jam\n📞 +966 55 222 1465";

// WhatsApp deep-link helper. Normalises phone to digits-only (keeps country code).
function waLink(phone, message) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  // If it looks like a Saudi local number starting with 05, prefix 966
  const normalized = digits.startsWith("0") ? `966${digits.slice(1)}` : digits;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
}

export default function CustomersPage() {
  const { t } = useLang();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTargets, setComposeTargets] = useState([]); // array of customer objects
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);

  useEffect(() => {
    api.get("/customers")
      .then((r) => setCustomers(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => c.phone.toLowerCase().includes(q));
  }, [customers, search]);

  const summary = useMemo(() => {
    const total = customers.length;
    const repeat = customers.filter((c) => c.visit_count > 1).length;
    const vip = customers.filter((c) => c.vip_visits > 0).length;
    return { total, repeat, vip };
  }, [customers]);

  function toggleSelect(phone) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  }
  function toggleSelectAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.phone)));
  }

  function openCompose(targets) {
    if (!targets.length) return toast.error(t("common.empty"));
    setComposeTargets(targets);
    setComposeOpen(true);
  }

  function sendAll() {
    composeTargets.forEach((c, i) => {
      const personalised = template.replace("{phone}", c.phone);
      const url = waLink(c.phone, personalised);
      // Stagger opens slightly to avoid popup blocking
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), i * 250);
    });
    toast.success(`${composeTargets.length} ${t("cust.selectedCount")}`);
    setComposeOpen(false);
    setSelected(new Set());
  }

  function sendSingle(c) {
    const personalised = template.replace("{phone}", c.phone);
    window.open(waLink(c.phone, personalised), "_blank", "noopener,noreferrer");
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— CRM</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("cust.title")}</h1>
          <p className="text-foreground/50 mt-1 max-w-xl">{t("cust.subtitle")}</p>
        </div>
        {selected.size > 0 && (
          <button
            data-testid="customers-send-bulk-btn"
            onClick={() => openCompose(customers.filter((c) => selected.has(c.phone)))}
            className="btn-gold"
          >
            <Send className="h-4 w-4" /> {t("cust.sendBulk")} ({selected.size})
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-luxury accent-gold p-5" data-testid="customers-total-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center">
              <UsersIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("cust.totalCustomers")}</div>
              <div className="text-3xl font-heading mt-1">{summary.total}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury accent-leaf p-5" data-testid="customers-repeat-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-leaf/15 text-leaf grid place-items-center">
              <Repeat className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("cust.repeatCustomers")}</div>
              <div className="text-3xl font-heading mt-1">{summary.repeat}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury p-5" data-testid="customers-vip-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center">
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("cust.vipCustomers")}</div>
              <div className="text-3xl font-heading mt-1">{summary.vip}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card-luxury p-4 flex items-center gap-3">
        <Search className="h-4 w-4 text-foreground/40 ms-2" />
        <input
          data-testid="customers-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("cust.searchPlaceholder")}
          className="flex-1 bg-transparent outline-none text-sm"
        />
        <button
          onClick={toggleSelectAll}
          data-testid="customers-select-all"
          className="text-xs text-gold hover:underline px-3 py-1.5 rounded-full border border-gold/30"
        >
          {t("cust.selectAll")}
        </button>
      </div>

      {/* Table */}
      <div className="card-luxury overflow-hidden">
        {/* DESKTOP / TABLET */}
        <div className="desktop-table overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
              <tr>
                <th className="px-4 py-3 w-10"></th>
                <th className="text-start px-4 py-3">{t("cust.phone")}</th>
                <th className="text-end px-4 py-3">{t("cust.visits")}</th>
                <th className="text-end px-4 py-3">{t("cust.totalSpent")}</th>
                <th className="text-start px-4 py-3">{t("cust.lastVisit")}</th>
                <th className="text-start px-4 py-3">{t("cust.favServices")}</th>
                <th className="text-end px-4 py-3">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center py-12 text-foreground/40">{t("common.loading")}</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center py-12 text-foreground/40">{t("common.empty")}</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.phone} className="border-t border-charcoal-600/40 hover:bg-gold/5" data-testid={`customer-row-${c.phone}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      data-testid={`customer-select-${c.phone}`}
                      checked={selected.has(c.phone)}
                      onChange={() => toggleSelect(c.phone)}
                      className="accent-gold h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.phone}</div>
                    <div className="text-xs text-foreground/40 mt-0.5">
                      {t("cust.firstVisit")}: {formatDate(c.first_visit)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <span className="font-medium">{c.visit_count}</span>
                    {c.visit_count > 1 && <span className="ms-1 text-xs text-leaf">★</span>}
                  </td>
                  <td className="px-4 py-3 text-end font-medium text-gold">{formatSAR(c.total_spent)}</td>
                  <td className="px-4 py-3 text-foreground/70 whitespace-nowrap">{formatDate(c.last_visit)}</td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="flex flex-wrap gap-1">
                      {c.services.slice(0, 3).map((s) => (
                        <span key={s} className="text-[10px] bg-charcoal-900 border border-gold/15 rounded-full px-2 py-0.5">{s}</span>
                      ))}
                      {c.services.length > 3 && (
                        <span className="text-[10px] text-foreground/40">+{c.services.length - 3}</span>
                      )}
                      {c.vip_visits > 0 && (
                        <span className="text-[10px] bg-gold/20 text-gold rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Star className="h-2.5 w-2.5 fill-gold" /> {t("cust.vipBadge")}
                        </span>
                      )}
                      {c.discounted_visits > 0 && (
                        <span className="text-[10px] bg-leaf/20 text-leaf rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <BadgePercent className="h-2.5 w-2.5" /> {t("cust.discountBadge")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <button
                      onClick={() => sendSingle(c)}
                      data-testid={`customer-send-${c.phone}`}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-leaf hover:bg-leaf-light text-white transition"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> {t("cust.sendPromo")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* MOBILE */}
        <div className="mobile-card-list p-3">
          {loading && <div className="text-center py-8 text-foreground/40">{t("common.loading")}</div>}
          {!loading && filtered.length === 0 && <div className="text-center py-8 text-foreground/40">{t("common.empty")}</div>}
          {filtered.map((c) => (
            <div key={c.phone} className="bg-charcoal-900/60 border border-gold/15 rounded-xl p-4" data-testid={`customer-mcard-${c.phone}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(c.phone)}
                    onChange={() => toggleSelect(c.phone)}
                    className="accent-gold h-4 w-4 mt-1"
                  />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.phone}</div>
                    <div className="text-[10px] text-foreground/50 mt-0.5">{formatDate(c.last_visit)}</div>
                  </div>
                </label>
                <div className="text-end shrink-0">
                  <div className="text-lg font-heading text-gold">{formatSAR(c.total_spent)}</div>
                  <div className="text-[10px] text-foreground/50">{c.visit_count} {t("cust.visits")}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {c.services.slice(0, 2).map((s) => (
                  <span key={s} className="text-[10px] bg-charcoal-800 border border-gold/15 rounded-full px-2 py-0.5">{s}</span>
                ))}
                {c.services.length > 2 && <span className="text-[10px] text-foreground/40 self-center">+{c.services.length - 2}</span>}
                {c.vip_visits > 0 && (
                  <span className="text-[10px] bg-gold/20 text-gold rounded-full px-2 py-0.5">★ VIP</span>
                )}
              </div>
              <button
                onClick={() => sendSingle(c)}
                className="w-full inline-flex items-center justify-center gap-2 text-sm py-2.5 rounded-full bg-leaf text-white font-medium active:scale-95 transition"
              >
                <MessageCircle className="h-4 w-4" /> {t("cust.sendPromo")}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Compose modal */}
      {composeOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setComposeOpen(false)}>
          <div className="card-luxury p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-heading flex items-center gap-2">
                <Send className="h-5 w-5 text-gold" /> {t("cust.composePromo")}
              </h2>
              <button onClick={() => setComposeOpen(false)}><X className="h-5 w-5" /></button>
            </div>

            <div className="text-xs text-foreground/60 mb-4">
              {composeTargets.length} {t("cust.selectedCount")} · {t("cust.messageHelp")}
            </div>

            <div className="flex flex-wrap gap-1 mb-4 max-h-24 overflow-y-auto scrollbar-thin">
              {composeTargets.map((c) => (
                <span key={c.phone} className="text-xs bg-charcoal-900 border border-gold/20 rounded-full px-2 py-1">
                  {c.phone}
                </span>
              ))}
            </div>

            <textarea
              data-testid="promo-message"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows="8"
              className="w-full bg-charcoal-900 border border-charcoal-600 focus:border-gold focus:ring-2 focus:ring-gold/30 rounded-lg px-4 py-3 outline-none text-sm font-mono"
            />
            <div className="text-[11px] text-foreground/40 mt-2">
              Tip: gunakan <code className="text-gold">{"{phone}"}</code> untuk personalisasi
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setComposeOpen(false)} className="btn-outline-gold flex-1 justify-center">
                {t("common.cancel")}
              </button>
              <button
                onClick={sendAll}
                data-testid="promo-send-all"
                className="btn-gold flex-1 justify-center"
              >
                <MessageCircle className="h-4 w-4" /> {t("cust.openWhatsapp")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
